import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CalendarDays, 
  Loader2, 
  CheckCircle, 
  ArrowRight,
  FileText,
  Image,
  Share2,
  Sparkles,
  Edit3,
  Eye,
  RefreshCw,
  AlertTriangle,
  Link2,
  XCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import GuidedProjectLayout, { useProjectSteps } from '@/components/ios26/GuidedProjectLayout';
import { GlassCard, PillButton, SectionHeader, StatusChip, ProgressIndicator } from '@/components/ios26/IOSComponents';
import '@/styles/ios26-design-system.css';

/**
 * P0 VERROUILLAGE: UUID generator for idempotency keys
 */
function generateIdempotencyKey() {
  return 'gen_' + crypto.randomUUID();
}

/**
 * P0 UX OVERHAUL v2: PrerequisitesBanner
 * ======================================
 * Displays missing prerequisites with CTAs to required steps
 */
function PrerequisitesBanner({ missingPrereqs, projectId }) {
  const navigate = useNavigate();
  
  if (!missingPrereqs || missingPrereqs.length === 0) return null;
  
  const stepRoutes = {
    connect: `/projects/${projectId}/setup/connect`,
    campaign: `/projects/${projectId}/setup/campaign`,
    analyze: `/projects/${projectId}/setup/analyze`
  };
  
  return (
    <div 
      className="mb-6 p-4 rounded-2xl border"
      style={{ 
        backgroundColor: 'hsl(var(--color-warning-bg))',
        borderColor: 'hsl(var(--color-warning) / 0.3)'
      }}
      data-testid="prerequisites-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle 
          className="w-5 h-5 flex-shrink-0 mt-0.5" 
          style={{ color: 'hsl(var(--color-warning-text))' }} 
        />
        <div className="flex-1">
          <h4 
            className="font-semibold text-sm mb-2"
            style={{ color: 'hsl(var(--color-warning-text))' }}
          >
            Prérequis manquants
          </h4>
          <ul className="space-y-2">
            {missingPrereqs.map((prereq, i) => (
              <li key={i} className="flex items-center justify-between">
                <span className="text-sm text-semantic-2">• {prereq.label}</span>
                <button
                  onClick={() => navigate(stepRoutes[prereq.stepId])}
                  className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
                  style={{ 
                    backgroundColor: 'hsl(var(--color-warning) / 0.2)',
                    color: 'hsl(var(--color-warning-text))'
                  }}
                  data-testid={`go-to-${prereq.stepId}`}
                >
                  <Link2 className="w-3 h-3" />
                  Aller à {prereq.stepId === 'connect' ? 'Connexion' : prereq.stepId === 'campaign' ? 'Campagne' : prereq.stepId}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * P0 VERROUILLAGE Point 3: Structured Error Handler
 */
function GenerationErrorBanner({ error, onRetry, onAction }) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  
  if (!error) return null;
  
  const handleAction = () => {
    switch (error.cta_action) {
      case 'go_to_connect':
        navigate(`/projects/${projectId}/setup/connect`);
        break;
      case 'go_to_campaign':
        navigate(`/projects/${projectId}/setup/campaign`);
        break;
      case 'go_to_analyze':
        navigate(`/projects/${projectId}/setup/analyze`);
        break;
      case 'open_reauth_modal':
        window.dispatchEvent(new CustomEvent('ame:open-platform-modal', {
          detail: { platform: error.details?.channels?.[0] || 'instagram' }
        }));
        break;
      case 'retry':
      case 'retry_with_new_key':
        onRetry?.();
        break;
      case 'poll_status':
        // Already polling
        break;
      default:
        onAction?.(error.cta_action, error.cta_payload);
    }
  };
  
  return (
    <div 
      className="mb-6 p-4 rounded-2xl border"
      style={{ 
        backgroundColor: 'hsl(var(--color-danger-bg))',
        borderColor: 'hsl(var(--color-danger) / 0.3)'
      }}
      data-testid="generation-error-banner"
    >
      <div className="flex items-start gap-3">
        <XCircle 
          className="w-5 h-5 flex-shrink-0 mt-0.5" 
          style={{ color: 'hsl(var(--color-danger))' }} 
        />
        <div className="flex-1">
          <h4 
            className="font-semibold text-sm mb-1"
            style={{ color: 'hsl(var(--color-danger))' }}
          >
            {error.message}
          </h4>
          {error.action_required && (
            <p className="text-sm text-semantic-2 mb-3">{error.action_required}</p>
          )}
          {error.cta_label && (
            <button
              onClick={handleAction}
              className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-full transition-colors"
              style={{ 
                backgroundColor: 'hsl(var(--color-danger) / 0.2)',
                color: 'hsl(var(--color-danger))'
              }}
              data-testid="error-cta-btn"
            >
              {error.cta_label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Step 4: Générer 30 jours de contenu
 * ===================================
 * P0 VERROUILLAGES:
 * 1. Idempotency key on generate
 * 2. Generation lock (UI disabled while in progress)
 * 3. Structured error handling
 * 4. Job polling for status
 */
function GenerateStepContent() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { completeStep, stepStatuses, getMissingPrerequisites, canExecuteDestructiveAction } = useProjectSteps();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  // iter 252 P0 — real generation summary from the backend job
  // (expected_items + result_summary.posts_created/errors_count),
  // replacing the previous hardcoded 4/30/24 counters derived only from
  // job.progress.
  const [genSummary, setGenSummary] = useState({
    progress: 0,
    state: null,
    expected: 0,
    persisted: 0,
    errors: 0
  });
  const [generatedContent, setGeneratedContent] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  
  // P0 VERROUILLAGE: State for job management
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const idempotencyKeyRef = useRef(null);
  const pollingRef = useRef(null);
  
  useEffect(() => {
    loadProject();
    return () => {
      // Cleanup polling on unmount
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [projectId]);
  
  const loadProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      const project = response.data;
      
      // Check if content already generated
      if (project.content_generated || project.status?.content_generated) {
        setIsComplete(true);
        
        // Load generated content
        let persistedCount = 0;
        try {
          const contentResponse = await apiClient.get(`/projects/${projectId}/contents`);
          const items = contentResponse.data || [];
          setGeneratedContent(items);
          persistedCount = items.length;
        } catch {
          // Content endpoint may not exist yet
        }
        
        setGenSummary({
          progress: 100,
          state: 'success',
          expected: project.last_generation?.contents_generated || persistedCount,
          persisted: project.last_generation?.contents_generated || persistedCount,
          errors: 0
        });
      }
      
      // Check if there's an active generation job
      if (project.last_generation?.batch_id) {
        const batchId = project.last_generation.batch_id;
        try {
          const statusResponse = await apiClient.get(`/calendar/generate/status?batch_id=${batchId}&project_id=${projectId}`);
          const jobStatus = statusResponse.data;
          
          // Check state using new stable contract
          if (jobStatus.state === 'queued' || jobStatus.state === 'running') {
            // Resume polling for this job
            setCurrentBatchId(batchId);
            setGenerating(true);
            startPolling(batchId);
          }
        } catch {
          // Job not found or expired
        }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  // P0 VERROUILLAGE Point 4: Polling with stable contract
  const startPolling = useCallback((batchId) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    
    pollingRef.current = setInterval(async () => {
      try {
        const response = await apiClient.get(`/calendar/generate/status?batch_id=${batchId}&project_id=${projectId}`);
        const job = response.data;
        
        // iter 252 — drive the UI from the REAL backend job, never from
        // hardcoded totals. result_summary is only populated on terminal
        // states, so persisted/errors show 0 while running (progress bar
        // reflects live advancement) and the true counts on completion.
        const summary = job.result_summary || {};
        setGenSummary({
          progress: job.progress || 0,
          state: job.state,
          expected: job.expected_items || 0,
          persisted: summary.posts_created ?? 0,
          errors: summary.errors_count ?? 0
        });
        
        // Check terminal states
        if (job.state === 'success') {
          // Generation complete
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGenerating(false);
          setIsComplete(true);
          setCurrentBatchId(null);
          completeStep('generate');
          
          const result = job.result_summary || {};
          const persisted = result.posts_created ?? 0;
          const expected = job.expected_items || 0;
          if (persisted === 0) {
            toast.error('Génération terminée mais aucun contenu persisté. Vérifiez les plateformes connectées.');
          } else if (expected && persisted < expected) {
            toast.warning(`${persisted}/${expected} contenus générés (partiel).`);
          } else {
            toast.success(`${persisted} contenus générés !`);
          }
          
          // Show warnings if any
          if (result.warnings?.length > 0) {
            result.warnings.forEach(w => toast.warning(w));
          }
          
          // Load generated content
          try {
            const contentsResponse = await apiClient.get(`/projects/${projectId}/contents`);
            setGeneratedContent(contentsResponse.data || []);
          } catch {
            setGeneratedContent([]);
          }
        } else if (job.state === 'failed') {
          // Generation failed
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setGenerating(false);
          setCurrentBatchId(null);
          
          setGenerationError({
            error: job.error?.code || 'TRANSIENT_ERROR',
            message: job.error?.message || 'La génération a échoué',
            action_required: 'Veuillez réessayer',
            cta_label: 'Réessayer',
            cta_action: 'retry'
          });
        }
      } catch (error) {
        console.error('Polling error:', error);
        // Don't stop polling on network errors, just log
      }
    }, 2000); // Poll every 2 seconds
  }, [projectId, completeStep]);
  
  const handleGenerate = async () => {
    // P0 UX OVERHAUL v2: Check prerequisites before allowing destructive action
    if (!canExecuteDestructiveAction('generate')) {
      toast.error('Veuillez compléter les prérequis avant de générer du contenu');
      return;
    }
    
    // P0 VERROUILLAGE Point 1: Generate idempotency key
    idempotencyKeyRef.current = generateIdempotencyKey();
    
    // Clear any previous error
    setGenerationError(null);
    setGenerating(true);
    
    try {
      // Reset summary - starting
      setGenSummary({ progress: 0, state: 'queued', expected: 0, persisted: 0, errors: 0 });
      
      // P0 VERROUILLAGE: Call with idempotency key header
      const response = await apiClient.post(
        `/calendar/generate`,
        {
          project_id: projectId,
          days: 30,
          posts_per_day: 1
        },
        {
          headers: {
            'Idempotency-Key': idempotencyKeyRef.current
          }
        }
      );
      
      // P0 VERROUILLAGE Point 4: Handle async job response
      if (response.status === 202) {
        // Job queued - start polling
        const batchId = response.data.batch_id;
        setCurrentBatchId(batchId);
        toast.info('Génération démarrée...');
        startPolling(batchId);
        return;
      }
      
      // Handle idempotent replay (200 with existing job)
      if (response.data.idempotent_replay) {
        toast.info('Requête déjà en cours de traitement');
        if (response.data.batch_id) {
          setCurrentBatchId(response.data.batch_id);
          startPolling(response.data.batch_id);
        }
        return;
      }
      
    } catch (error) {
      setGenerating(false);
      
      // P0 VERROUILLAGE Point 3: Handle structured errors
      if (error.response?.data?.error) {
        const errorData = error.response.data;
        setGenerationError(errorData);
        
        // Show specific toast based on error type
        switch (errorData.error) {
          case 'GENERATION_IN_PROGRESS':
            toast.info('Une génération est déjà en cours');
            if (errorData.details?.batch_id) {
              setCurrentBatchId(errorData.details.batch_id);
              startPolling(errorData.details.batch_id);
            }
            break;
          case 'NO_CHANNELS_CONNECTED':
            toast.error('Connectez au moins un canal de diffusion');
            break;
          case 'CAMPAIGN_NOT_CONFIGURED':
            toast.error('Configurez d\'abord votre campagne');
            break;
          case 'THEMES_NOT_VALIDATED':
            toast.error('Validez d\'abord vos thèmes marketing');
            break;
          case 'REAUTH_REQUIRED':
            toast.error('Reconnexion requise pour un canal');
            break;
          case 'IDEMPOTENCY_CONFLICT':
            toast.error('Requête en double détectée. Veuillez réessayer.');
            idempotencyKeyRef.current = null; // Force new key on retry
            break;
          default:
            toast.error(errorData.message || 'Erreur lors de la génération');
        }
      } else {
        // Fallback for non-structured errors
        toast.error(error.response?.data?.detail || 'Erreur lors de la génération');
        setGenerationError({
          error: 'TRANSIENT_ERROR',
          message: 'Erreur lors de la génération',
          action_required: 'Veuillez réessayer',
          cta_label: 'Réessayer',
          cta_action: 'retry'
        });
      }
      
      setProgress({
        articles: { total: 0, done: 0, status: 'pending' },
        posts: { total: 0, done: 0, status: 'pending' },
        visuals: { total: 0, done: 0, status: 'pending' }
      });
    }
  };
  
  // P0 VERROUILLAGE: Retry handler with new idempotency key
  const handleRetry = () => {
    idempotencyKeyRef.current = null; // Force new key
    setGenerationError(null);
    handleGenerate();
  };
  
  const handleContinue = () => {
    navigate(`/projects/${projectId}/setup/calendar`);
  };
  
  const handleReview = () => {
    navigate(`/projects/${projectId}/setup/calendar`);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }
  
  // P0 UX OVERHAUL v2: Get missing prerequisites
  const missingPrereqs = getMissingPrerequisites('generate');
  const canGenerate = canExecuteDestructiveAction('generate');
  
  return (
    <div className="max-w-3xl mx-auto">
      <SectionHeader
        title="Générer 30 jours de contenu"
        subtitle="L'IA crée automatiquement un mois de publications"
      />
      
      {/* P0 UX OVERHAUL v2: Prerequisites Banner */}
      <PrerequisitesBanner missingPrereqs={missingPrereqs} projectId={projectId} />
      
      {/* P0 VERROUILLAGE Point 3: Structured Error Banner */}
      <GenerationErrorBanner 
        error={generationError} 
        onRetry={handleRetry}
      />
      
      {/* P0 VERROUILLAGE Point 2: Generation In Progress Banner */}
      {generating && currentBatchId && (
        <div 
          className="mb-6 p-4 rounded-2xl border"
          style={{ 
            backgroundColor: 'hsl(var(--color-info-bg))',
            borderColor: 'hsl(var(--color-info) / 0.3)'
          }}
          data-testid="generation-in-progress-banner"
        >
          <div className="flex items-center gap-3">
            <Loader2 
              className="w-5 h-5 animate-spin flex-shrink-0" 
              style={{ color: 'hsl(var(--color-info-text))' }} 
            />
            <div className="flex-1">
              <h4 
                className="font-semibold text-sm"
                style={{ color: 'hsl(var(--color-info-text))' }}
              >
                Génération en cours...
              </h4>
              <p className="text-xs text-semantic-muted mt-0.5">
                ID: {currentBatchId.slice(0, 8)}... • Ne fermez pas cette page
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Action Card */}
      {!generating && !isComplete && (
        <GlassCard className="p-8 text-center mb-8">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center mx-auto mb-6">
            <CalendarDays className="w-10 h-10 text-white" />
          </div>
          
          <h3 className="text-xl font-bold text-semantic mb-3">
            Prêt à générer votre contenu ?
          </h3>
          <p className="text-semantic-2 mb-6 max-w-md mx-auto">
            L&apos;IA va créer 30 jours de publications adaptées à vos objectifs. 
            Vous pourrez tout revoir et modifier avant publication.
          </p>
          
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-sm text-blue-700">
              <FileText className="w-4 h-4" />
              4 articles de blog
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-purple-50 rounded-full text-sm text-purple-700">
              <Share2 className="w-4 h-4" />
              30 posts sociaux
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-emerald-50 rounded-full text-sm text-emerald-700">
              <Image className="w-4 h-4" />
              30 visuels
            </div>
          </div>
          
          <PillButton
            variant="primary"
            size="lg"
            onClick={handleGenerate}
            disabled={!canGenerate}
            icon={<Sparkles className="w-5 h-5" />}
            data-testid="generate-content-btn"
          >
            Générer 30 jours de contenu
          </PillButton>
          
          {!canGenerate && (
            <p className="text-xs text-semantic-muted mt-3">
              Complétez les prérequis ci-dessus pour activer la génération
            </p>
          )}
        </GlassCard>
      )}
      
      {/* Generation Progress — iter 252: real backend counters */}
      {(generating || isComplete) && (
        <div className="space-y-4 mb-8">
          <GlassCard className="p-5" data-testid="generation-summary-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-10 h-10 rounded-xl flex items-center justify-center',
                  genSummary.state === 'success' ? 'bg-emerald-100' : 'bg-blue-100'
                )}>
                  {genSummary.state === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  )}
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Génération du contenu</h4>
                  <p className="text-sm text-gray-500">
                    {genSummary.state === 'success'
                      ? 'Génération terminée'
                      : `Génération en cours… ${genSummary.progress}%`}
                  </p>
                </div>
              </div>
              <StatusChip status={genSummary.state === 'success' ? 'success' : 'pending'}>
                {genSummary.state === 'success' ? '✅ Terminé' : '⏳ En cours'}
              </StatusChip>
            </div>

            {/* Live progress bar */}
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden mb-4">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  genSummary.state === 'success' ? 'bg-emerald-500' : 'bg-blue-500'
                )}
                style={{ width: `${genSummary.progress}%` }}
              />
            </div>

            {/* Real counters */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-gray-50 p-3 text-center" data-testid="gen-expected">
                <div className="text-2xl font-bold text-gray-900">{genSummary.expected || '—'}</div>
                <div className="text-xs text-gray-500">Attendus</div>
              </div>
              <div className="rounded-xl bg-emerald-50 p-3 text-center" data-testid="gen-persisted">
                <div className="text-2xl font-bold text-emerald-600">{genSummary.persisted}</div>
                <div className="text-xs text-emerald-600">Persistés</div>
              </div>
              <div className={cn(
                'rounded-xl p-3 text-center',
                genSummary.errors > 0 ? 'bg-red-50' : 'bg-gray-50'
              )} data-testid="gen-errors">
                <div className={cn(
                  'text-2xl font-bold',
                  genSummary.errors > 0 ? 'text-red-600' : 'text-gray-400'
                )}>{genSummary.errors}</div>
                <div className={cn('text-xs', genSummary.errors > 0 ? 'text-red-600' : 'text-gray-500')}>En erreur</div>
              </div>
            </div>
          </GlassCard>
        </div>
      )}
      
      {/* Complete State Actions */}
      {isComplete && (() => {
        const expected = genSummary.expected || 0;
        const persisted = genSummary.persisted || 0;
        const isPartial = expected > 0 && persisted > 0 && persisted < expected;
        const isEmpty = persisted === 0 && expected > 0;
        return (
        <div className="space-y-4 ios-animate-fade-in">
          {/* Outcome banner — honest about partial / empty results */}
          {isEmpty ? (
            <GlassCard className="p-5 bg-gradient-to-r from-red-50 to-orange-50 border-red-200" data-testid="gen-banner-empty">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-500 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Aucun contenu persisté</h3>
                  <p className="text-sm text-gray-600">
                    Le job s&apos;est terminé mais aucun contenu n&apos;a été enregistré. Vérifiez les plateformes connectées puis relancez.
                  </p>
                </div>
              </div>
            </GlassCard>
          ) : isPartial ? (
            <GlassCard className="p-5 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200" data-testid="gen-banner-partial">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Terminé avec erreurs ({persisted}/{expected})</h3>
                  <p className="text-sm text-gray-600">
                    Une partie du contenu a été générée. Vous pouvez revoir les publications disponibles ou relancer.
                  </p>
                </div>
              </div>
            </GlassCard>
          ) : (
            <GlassCard className="p-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="gen-banner-ready">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">Contenu prêt !</h3>
                  <p className="text-sm text-gray-600">
                    {persisted > 0 ? `${persisted} contenu${persisted > 1 ? 's' : ''} généré${persisted > 1 ? 's' : ''}. ` : ''}Vous pouvez revoir et modifier chaque publication avant la mise en ligne.
                  </p>
                </div>
              </div>
            </GlassCard>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-3">
            <PillButton
              variant="secondary"
              size="lg"
              className="flex-1"
              onClick={handleReview}
              icon={<Edit3 className="w-5 h-5" />}
            >
              Revoir / Modifier
            </PillButton>
            <PillButton
              variant="primary"
              size="lg"
              className="flex-1"
              onClick={handleContinue}
              icon={<ArrowRight className="w-5 h-5" />}
              iconPosition="right"
            >
              Continuer
            </PillButton>
          </div>
        </div>
        );
      })()}
    </div>
  );
}

/**
 * Step 4 Page Wrapper
 */
export default function GenerateStep() {
  return (
    <GuidedProjectLayout>
      <GenerateStepContent />
    </GuidedProjectLayout>
  );
}
